<script>
/**
 * VTagSelectSearch
 * @version 1.0.0
 * @since
 */

import EzInput from '@/components/ui/Input';
import EzCheckbox from '@/components/ui/Checkbox';
import { debounce } from '@/util/utils';
import VTag from '@/components/v3/elements/VTag';
import EzButton from '@/components/ui/Button';
import { NUMBER_OF_TAGS_TO_DISPLAY, TAGS_DISPLAY_TRESHOLD, TagMatchType } from '@/util/constants';

export default {
  components: {
    EzInput,
    EzCheckbox,
    VTag,
    EzButton,
  },
  props: {
    selectedTags: {
      required: false,
      type: Array,
      default: () => [],
    },
    unselectedTags: {
      required: true,
      type: Array,
      default: () => [],
    },
    /**
     * When true, renders the ALL / ANY match selector in the selected-tags header.
     */
    supportsMatchType: {
      required: false,
      type: Boolean,
      default: false,
    },
    /**
     * Current match mode: 'all' (product must have every selected tag)
     * or 'any' (product must have at least one).
     */
    matchType: {
      required: false,
      type: String,
      default: TagMatchType.ALL,
      validator: value => [TagMatchType.ALL, TagMatchType.ANY].includes(value),
    },
  },
  data() {
    return {
      term: '',
      tagsListExpanded: false,
      matchTypeRenderKey: 0,
    };
  },
  computed: {
    // Exposed so the template passes the constants instead of string literals.
    matchTypes() {
      return TagMatchType;
    },
    isMatchTypeVisible() {
      // Meaningless with fewer than two tags selected.
      return this.supportsMatchType && this.selectedTags.length > 1;
    },
    isMatchAll() {
      return this.matchType === TagMatchType.ALL;
    },
    isMatchAny() {
      return this.matchType === TagMatchType.ANY;
    },
    filteredTags() {
      return this.unselectedTags.filter(tag => tag.name.toLowerCase().includes(this.term.toLowerCase()));
    },
    numberOfTagsToDisplay() {
      if (this.filteredTags.length - NUMBER_OF_TAGS_TO_DISPLAY > TAGS_DISPLAY_TRESHOLD) return NUMBER_OF_TAGS_TO_DISPLAY;
      return this.filteredTags.length;
    },
    filteredTagsToDisplay() {
      if (!this.tagsListExpanded) {
        return this.filteredTags.slice(0, this.numberOfTagsToDisplay);
      }
      return this.filteredTags;
    },
    expandButtonCopy() {
      return this.tagsListExpanded ? 'Collapse List' : 'Show more tags';
    },
    expandButtonIcon() {
      return this.tagsListExpanded ? 'angle-up' : 'angle-down';
    },
  },
  methods: {
    resetSearch() {
      this.term = '';
    },
    onClick(tag, isUnselect) {
      this.$emit('change', {
        tag,
        isUnselect,
      });
    },
    onSearch: debounce(async function deb(term) {
      if (term !== this.term) {
        this.term = term;
      }
    }, 400),
    toggleListExpanded() {
      this.tagsListExpanded = !this.tagsListExpanded;
    },
    /**
     * The two options are mutually exclusive. The native checkbox toggles itself
     * on click, so when `matchType` doesn't change (re-clicking the active
     * option) Vue patches nothing and the DOM drifts out of sync with the prop.
     * Bumping the render key re-mounts both inputs from `checked`.
     */
    onMatchTypeChange(value) {
      this.matchTypeRenderKey += 1;
      if (value !== this.matchType) {
        this.$emit('matchTypeChange', value);
      }
    },
  },
};
</script>

<template>
  <div>
    <div v-if="selectedTags.length" class="tags-wrapper tags-wrapper__selected">
      <div class="tags-header">
        <div class="tags-label tags-label--inline">
          <span class="tags-label__number">{{ selectedTags.length }}</span> selected {{ 'tag' | pluralize(selectedTags.length) }}
        </div>
        <div v-if="isMatchTypeVisible" class="match-type">
          <ez-checkbox
            class="match-type__option height-auto"
            :key="`matchAll-${matchTypeRenderKey}`"
            :checked="isMatchAll"
            label="All"
            name="tagMatchAll"
            @change="onMatchTypeChange(matchTypes.ALL)"
          />
          <ez-checkbox
            class="match-type__option height-auto"
            :key="`matchAny-${matchTypeRenderKey}`"
            :checked="isMatchAny"
            label="Any"
            name="tagMatchAny"
            @change="onMatchTypeChange(matchTypes.ANY)"
          />
        </div>
      </div>
      <div class="all-tags">
        <v-tag v-for="tag in selectedTags" :key=tag.id :color=tag.color.colors[0] :backgroundColor=tag.color.colors[1]
               @click="onClick(tag, true)" :canRemove=true>
          {{ tag.name }}
        </v-tag>
      </div>
    </div>
    <div class="tags-wrapper">
      <form @submit.prevent>
        <ez-input v-if="unselectedTags.length" :value="term" formKey="filters" name="search" label="Available Tags" class="search"
                  :placeholder="`Search for a ${$t('global.tag')}`" @onChange="onSearch">
          <template #prefix>
            <font-awesome-icon icon="search" />
          </template>
        </ez-input>
      </form>
      <div v-if="filteredTags.length" class="all-tags">
        <v-tag
          v-for="tag in filteredTagsToDisplay"
          :key=tag.id :color=tag.color.colors[0]
          :backgroundColor=tag.color.colors[1]
          @click="onClick(tag)">
          {{ tag.name }}
        </v-tag>
        <span
          class="tags-number-indicator"
          v-if="filteredTags.length > numberOfTagsToDisplay && !tagsListExpanded"
        >
          +{{ filteredTags.length - numberOfTagsToDisplay }}
        </span>
      </div>
      <ez-button
        v-if="filteredTags.length > numberOfTagsToDisplay"
        class="u-flex-h-center mt-16"
        @click="toggleListExpanded"
        isFullWidth
        type="link"
        formType="button"
      >
        <span>{{ expandButtonCopy }}</span>
        <font-awesome-icon class="ml-8" :icon="expandButtonIcon" />
      </ez-button>
      <div v-if="!filteredTags.length" class="tags-label">
        No more tags to show
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tags-label {
  @include font-size($label-font-size, 18px);
  color: $color-gray-6C;
  margin-bottom: 16px;

  &__number {
    font-weight: 700;
  }

  &--inline {
    margin-bottom: 0;
    flex-shrink: 0;
  }
}

.tags-header {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 16px;
  margin-bottom: 16px;
}

.match-type {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: 16px;

  &__option {
    flex: 0 0 auto;
    line-height: 18px;
  }
}

.tags-number-indicator {
  @include font-size(14px, 16px);
  color: $color-gray-6C;
}

.all-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.tags-wrapper {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px dashed $color-gray-E1;
}
</style>
